import React, { useState } from 'react';
import { motion } from 'framer-motion';

const ContactSection: React.FC = () => {
  const [formData, setFormData] = useState({ name: '', message: '' });
  const [showHearts, setShowHearts] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.name && formData.message) {
      setShowHearts(true);
      setShowThankYou(true);
      
      // Reset form
      setFormData({ name: '', message: '' });
      
      // Hide effects after a while
      setTimeout(() => {
        setShowHearts(false);
        setShowThankYou(false);
      }, 4000);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const funFacts = [
    'Loves rainbow colors',
    'Enjoys bedtime stories',
    'Dreams of flying',
    'Collects pretty stones',
    'Likes helping others'
  ];

  return (
    <section className="contact" id="contact">
      <div className="container">
        <motion.h2
          className="section-title"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          Say Hello to Krisha!
        </motion.h2>

        <motion.p
          className="section-subtitle"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
        >
          Send a message or just say hi! 💌
        </motion.p>

        <motion.div
          className="contact-content"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <motion.div
            className="contact-form"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.3 }}
          >
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Your Name</label>
                <motion.input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="What's your name?"
                  required
                  whileFocus={{ scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                />
              </div>
              <div className="form-group">
                <label htmlFor="message">Your Message</label>
                <motion.textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Write something nice for Krisha!"
                  required
                  whileFocus={{ scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                />
              </div>
              <motion.button
                type="submit"
                className="btn btn-primary"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                Send Love 💕
              </motion.button>
            </form>
          </motion.div>

          <motion.div
            className="contact-info"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.3 }}
          >
            <h3>🌟 Fun Facts About Krisha</h3>
            <ul>
              {funFacts.map((fact, index) => (
                <motion.li
                  key={index}
                  whileHover={{ x: 10 }}
                  transition={{ duration: 0.2 }}
                >
                  {fact}
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </motion.div>
      </div>

      {/* Floating Hearts Animation */}
      {showHearts && (
        <div className="floating-hearts">
          {[...Array(10)].map((_, i) => (
            <motion.div
              key={i}
              className="floating-heart"
              initial={{ 
                opacity: 1, 
                y: 0, 
                x: Math.random() * window.innerWidth,
                scale: 0.5
              }}
              animate={{ 
                opacity: 0, 
                y: -window.innerHeight, 
                scale: 1.5,
                rotate: 360
              }}
              transition={{ 
                duration: 4, 
                delay: i * 0.3,
                ease: "easeOut"
              }}
              style={{
                position: 'fixed',
                bottom: 0,
                fontSize: '2rem',
                pointerEvents: 'none',
                zIndex: 1000
              }}
            >
              💖
            </motion.div>
          ))}
        </div>
      )}

      {/* Thank You Message */}
      {showThankYou && (
        <motion.div
          className="thank-you-message"
          initial={{ opacity: 0, scale: 0.5, y: 50 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.5, y: 50 }}
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'linear-gradient(135deg, #ff6b9d, #a855f7)',
            color: 'white',
            padding: '30px 40px',
            borderRadius: '25px',
            fontSize: '1.5rem',
            fontWeight: '600',
            textAlign: 'center',
            zIndex: 1001,
            boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
          }}
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            🎉 Thank you {formData.name}! 🎉
            <br />
            Krisha loves your message! 💕
          </motion.div>
        </motion.div>
      )}
    </section>
  );
};

export default ContactSection;
